import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAdminAuth, AdminPermission } from '../../../hooks/admin/useAdminAuth';

interface AdminRouteGuardProps {
  children: ReactNode;
  requiredRole?: 'ADMIN' | 'SUPER_ADMIN' | 'MODERATOR' | 'MEMBER' | ('ADMIN' | 'SUPER_ADMIN' | 'MODERATOR' | 'MEMBER')[];
  requiredPermission?: AdminPermission | AdminPermission[];
  fallback?: ReactNode;
  redirectTo?: string;
}

const AdminRouteGuard: React.FC<AdminRouteGuardProps> = ({
  children,
  requiredRole,
  requiredPermission,
  fallback,
  redirectTo = '/login'  // Changed from /admin/login to unified /login
}) => {
  const { user, isLoading, isAuthenticated, hasRole, checkPermission } = useAdminAuth();
  const location = useLocation();

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">กำลังตรวจสอบสิทธิ์การเข้าถึง...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // 🚨 CRITICAL: Block MEMBER role from accessing admin routes
  // MEMBER should NEVER access /admin/* routes - redirect to homepage
  if (user.role === 'MEMBER') {
    return <Navigate to="/" replace />;
  }

  // Check role requirements
  if (requiredRole && !hasRole(requiredRole)) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return <Navigate to="/admin/unauthorized" replace />;
  }

  // Check permission requirements
  if (requiredPermission) {
    const permissions = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
    const hasAllPermissions = permissions.every(permission => checkPermission(permission));

    if (!hasAllPermissions) {
      if (fallback) {
        return <>{fallback}</>;
      }
      return <Navigate to="/admin/unauthorized" replace />;
    }
  }

  // All checks passed, render children
  return <>{children}</>;
};

export default AdminRouteGuard;

// Convenience component for unauthorized access
export const UnauthorizedPage: React.FC = () => {
  const { user, logout } = useAdminAuth();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
          <svg
            className="h-8 w-8 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          ไม่มีสิทธิ์เข้าถึง
        </h2>

        <p className="text-gray-600 mb-6">
          คุณไม่มีสิทธิ์ในการเข้าถึงหน้านี้ กรุณาติดต่อผู้ดูแลระบบ
        </p>

        <div className="space-y-4">
          <div className="bg-gray-100 p-4 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>ผู้ใช้งาน:</strong> {user?.name}
            </p>
            <p className="text-sm text-gray-700">
              <strong>บทบาท:</strong> {user?.role}
            </p>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => window.history.back()}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
            >
              ย้อนกลับ
            </button>
            <button
              onClick={() => logout()}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
            >
              ออกจากระบบ
            </button>
          </div>

          <a
            href="/admin/dashboard"
            className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors text-center"
          >
            กลับหน้าหลัก
          </a>
        </div>
      </div>
    </div>
  );
};

// HOC for protecting admin routes
export function withAdminAuth<T extends {}>(
  Component: React.ComponentType<T>,
  options?: {
    requiredRole?: AdminRouteGuardProps['requiredRole'];
    requiredPermission?: AdminRouteGuardProps['requiredPermission'];
  }
) {
  return function AuthenticatedComponent(props: T) {
    return (
      <AdminRouteGuard {...options}>
        <Component {...props} />
      </AdminRouteGuard>
    );
  };
}

// Hook for checking permissions in components
export function useAdminPermissions() {
  const { checkPermission, hasRole, user } = useAdminAuth();

  const canAccess = (
    permission?: AdminPermission,
    role?: 'ADMIN' | 'SUPER_ADMIN' | 'MODERATOR' | 'MEMBER'
  ): boolean => {
    if (role && !hasRole(role)) return false;
    if (permission && !checkPermission(permission)) return false;
    return true;
  };

  const canAccessAny = (
    permissions?: AdminPermission[],
    roles?: ('ADMIN' | 'SUPER_ADMIN' | 'MODERATOR' | 'MEMBER')[]
  ): boolean => {
    if (roles && roles.length > 0 && !hasRole(roles)) return false;
    if (permissions && permissions.length > 0) {
      return permissions.some(permission => checkPermission(permission));
    }
    return true;
  };

  const canAccessAll = (
    permissions?: AdminPermission[],
    roles?: ('ADMIN' | 'SUPER_ADMIN' | 'MODERATOR' | 'MEMBER')[]
  ): boolean => {
    if (roles && roles.length > 0 && !hasRole(roles)) return false;
    if (permissions && permissions.length > 0) {
      return permissions.every(permission => checkPermission(permission));
    }
    return true;
  };

  return {
    canAccess,
    canAccessAny,
    canAccessAll,
    user,
    hasRole,
    checkPermission,
  };
}